import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import {
  ClientTranscriptionControllerService,
  ManagementTranscriptionControllerService,
  UpdateTranscriptionRequest,
  CreateTranscriptionContentRequest,
  UpdateTranscriptionContentRequest,
  TranscriptionResponse,
  TranscriptionContentResponse,
  FindAndReplaceRequest,
  ManagementAutoTagTranscriptionsRequest
} from 'api-transcription';
import { SharedService } from '../../../services/shared.service';
import { SpellcheckService } from '../../../services/spellcheck.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { take } from 'rxjs/operators';
import { VideoTimelineComponent } from '../video-timeline/video-timeline.component';
import { TranscriptionsToolsComponent } from '../transcriptions-tools/transcriptions-tools.component';

@Component({
  selector: 'app-shared-transcriptions-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    TranslateModule,
    VideoTimelineComponent
  ],
  templateUrl: './transcriptions-form.component.html',
  styleUrl: './transcriptions-form.component.scss'
})
export class TranscriptionsFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() itemForEdit: TranscriptionResponse | null = null;
  @Input() mode: 'client' | 'admin' = 'client';
  // Deep-link only: seconds to start the video at (from the ?t= param on the
  // keyword-notification email link). Null for every normal open.
  @Input() initialSeconds: number | null = null;
  @Output() actionRequested: EventEmitter<any> = new EventEmitter();

  transcription: TranscriptionResponse | null = null;
  editingField: { contentIndex: number; field: string } | null = null;
  editingFileName = false;
  isFormVisible = false;
  private escapePressed = false;
  private originalValue: any = null;

  timelineCurrentTime: number = 0;
  currentSegmentIndex: number = -1;
  totalDuration: number = 0;
  isVideoVisible: boolean = true;
  captionsEnabled: boolean = false;
  isTextMerged: boolean = false;

  // Spellcheck properties
  private spellcheckTimer: any;

  // Recording properties (for admin mode)
  isRecording = false;
  isProcessingRecording = false;
  apiLoadingTools = false;
  recordingDuration = '00:00';
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recordingStartTime: number = 0;
  private recordingTimer: any;

  @ViewChild('videoTimeline') videoTimeline?: VideoTimelineComponent;

  constructor(
    private apiClientTranscriptions: ClientTranscriptionControllerService,
    private apiMgmtTranscriptions: ManagementTranscriptionControllerService,
    private sharedService: SharedService,
    private spellcheckService: SpellcheckService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.initializeTranscription();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['itemForEdit']) {
      this.initializeTranscription();

      if (this.itemForEdit) {
        this.isFormVisible = true;
      } else {
        this.isFormVisible = false;
      }
    }
  }

  private initializeTranscription() {
    if (this.itemForEdit) {
      this.transcription = JSON.parse(JSON.stringify(this.itemForEdit));
      this.calculateTotalDuration();

      if (this.transcription?.lang) {
        this.spellcheckService.initializeLanguage(this.transcription.lang);
      }
    } else {
      this.transcription = null;
      this.totalDuration = 0;
    }
    this.editingField = null;
    this.editingFileName = false;
    // initialSeconds is in seconds (from the URL); the player works in milliseconds.
    this.timelineCurrentTime = this.initialSeconds != null ? this.initialSeconds * 1000 : 0;
    this.currentSegmentIndex = -1;
  }

  private sortContentByStartTime() {
    if (this.transcription?.content) {
      this.transcription.content.sort((a, b) => (a.startTime || 0) - (b.startTime || 0));
      this.calculateTotalDuration();
      this.transcription = { ...this.transcription };
    }
  }

  private calculateTotalDuration() {
    if (!this.transcription?.content || this.transcription.content.length === 0) {
      this.totalDuration = 0;
      return;
    }

    const maxEndTime = Math.max(
      ...this.transcription.content.map(item => item.endTime || 0)
    );

    this.totalDuration = maxEndTime + 5000;
  }

  formatTime(miliseconds: number | undefined): string {
    if (miliseconds === undefined || miliseconds === null) return '00:00:000';

    const minutes = Math.floor(miliseconds / 60000);
    const remainingSeconds = Math.floor((miliseconds % 60000) / 1000);
    const remainingMs = miliseconds % 1000;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}:${remainingMs.toString().padStart(3, '0')}`;
  }

  parseTimeInput(timeString: string): number | null {
    if (!timeString || !timeString.trim()) return null;

    const trimmed = timeString.trim();

    if (trimmed.includes(':')) {
      const parts = trimmed.split(':');

      if (parts.length === 3) {
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);
        const milliseconds = parseInt(parts[2], 10);

        if (isNaN(minutes) || isNaN(seconds) || isNaN(milliseconds)) {
          return null;
        }

        if (seconds >= 60 || seconds < 0 || minutes < 0 || milliseconds < 0 || milliseconds >= 1000) {
          return null;
        }

        return (minutes * 60 + seconds) * 1000 + milliseconds;
      } else if (parts.length === 2) {
        const minutes = parseInt(parts[0], 10);
        const seconds = parseInt(parts[1], 10);

        if (isNaN(minutes) || isNaN(seconds)) {
          return null;
        }

        if (seconds >= 60 || seconds < 0 || minutes < 0) {
          return null;
        }

        return (minutes * 60 + seconds) * 1000;
      }
    }

    const parsed = parseInt(trimmed, 10);
    return isNaN(parsed) ? null : parsed;
  }

  onTimeInput(contentIndex: number, field: string, _event: Event) {
    if (!this.transcription || !this.transcription.content) return;

    const contentItem = this.transcription.content[contentIndex];
    if (contentItem && this.originalValue === null) {
      this.originalValue = (contentItem as any)[field];
    }
  }

  onTimeKeydown(event: KeyboardEvent) {
    const key = event.key;

    // Allow all navigation and editing keys
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter', 'Escape', 'Ctrl', 'Meta', 'Shift'
    ];

    // Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (allowedKeys.includes(key)) {
      return;
    }

    // Allow colons and digits
    if (key === ':' || /^\d$/.test(key)) {
      return;
    }

    // Block everything else
    event.preventDefault();
  }

  onTimeDisplayClick(contentIndex: number, field: string, time: number | undefined) {
    if (time !== undefined) {
      this.timelineCurrentTime = time;
    }

    this.startEditing(contentIndex, field);
  }

  startEditing(contentIndex: number, field: string) {
    const alreadyEditing = this.editingField?.contentIndex === contentIndex && this.editingField?.field === field;

    this.editingField = { contentIndex, field };
    if (!alreadyEditing) {
      this.originalValue = null;
    }

    setTimeout(() => {
      if (field === 'text' && this.transcription?.content) {
        const textElement = document.querySelector('p.text-display.editing') as HTMLElement;
        if (textElement) {
          textElement.textContent = this.transcription.content[contentIndex].text || '';

          if (this.transcription?.lang) {
            this.applySpellcheckHighlighting(textElement, this.transcription.lang);
          }
        }
      }
      this.focusEditingField(field, !alreadyEditing);
    }, 0);
  }

  startEditingFileName() {
    this.editingFileName = true;

    setTimeout(() => {
      this.focusEditingField('fileName');
    }, 0);
  }

  private focusEditingField(field: string, selectAll: boolean = true) {
    let selector = '';

    switch (field) {
      case 'startTime':
        selector = 'input.time-input';
        break;
      case 'endTime':
        selector = 'input.time-input';
        break;
      case 'text':
        selector = 'p.text-display.editing';
        break;
      case 'fileName':
        selector = 'input.filename-input';
        break;
    }

    if (selector) {
      const element = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | HTMLElement;
      if (element) {
        element.focus();
        if (field === 'text' && element.textContent && selectAll) {
          const range = document.createRange();
          range.selectNodeContents(element);
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else if ('select' in element && selectAll) {
          (element as HTMLInputElement).select();
        }
      }
    }
  }

  onFieldInput(contentIndex: number, field: string, event: Event) {
    if (!this.transcription || !this.transcription.content) return;

    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLElement;
    let newValue: any;

    if (field === 'text' && target.contentEditable === 'true') {
      newValue = target.textContent || '';

      if (this.spellcheckTimer) {
        clearTimeout(this.spellcheckTimer);
      }

      if (this.transcription.lang) {
        this.spellcheckTimer = setTimeout(() => {
          this.applySpellcheckHighlighting(target, this.transcription!.lang!);
        }, 500);
      }
    } else if (field === 'startTime' || field === 'endTime') {
      const parsedTime = this.parseTimeInput((target as HTMLInputElement).value);
      if (parsedTime !== null) {
        newValue = parsedTime;
      } else {
        return;
      }
    } else {
      newValue = (target as HTMLInputElement).value;
    }

    const contentItem = this.transcription.content[contentIndex];
    if (contentItem && this.originalValue === null) {
      this.originalValue = (contentItem as any)[field];
    }

    if (contentItem) {
      (contentItem as any)[field] = newValue;
    }
  }

  private applySpellcheckHighlighting(element: HTMLElement, lang: 'MK' | 'EN' | 'AL') {
    const text = element.textContent || '';
    const misspelledWords = this.spellcheckService.checkText(text, lang);

    const selection = window.getSelection();
    let cursorOffset = 0;
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      cursorOffset = preCaretRange.toString().length;
    }

    if (misspelledWords.length === 0) {
      if (element.querySelector('.misspelled')) {
        element.textContent = text;
        this.setCursorPosition(element, cursorOffset);
      }
      return;
    }

    let html = '';
    let lastIndex = 0;

    misspelledWords.forEach(({word, index}) => {
      html += this.escapeHtml(text.substring(lastIndex, index));
      html += `<span class="misspelled">${this.escapeHtml(word)}</span>`;
      lastIndex = index + word.length;
    });
    html += this.escapeHtml(text.substring(lastIndex));

    element.innerHTML = html;
    this.setCursorPosition(element, cursorOffset);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private setCursorPosition(element: HTMLElement, offset: number) {
    const selection = window.getSelection();
    if (!selection) return;

    let charCount = 0;
    const nodeStack: Node[] = [element];

    while (nodeStack.length > 0) {
      const node = nodeStack.pop()!;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length || 0;
        if (charCount + textLength >= offset) {
          const range = document.createRange();
          range.setStart(node, Math.min(offset - charCount, textLength));
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          return;
        }
        charCount += textLength;
      } else {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
    }

    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  onFieldBlur(contentIndex: number, field: string, event: Event) {
    if (!this.transcription || !this.transcription.content) return;

    if (this.escapePressed) {
      this.escapePressed = false;
      this.editingField = null;
      this.originalValue = null;
      return;
    }

    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLElement;
    let newValue: any;

    if (field === 'text' && target.contentEditable === 'true') {
      newValue = target.textContent || '';
    } else if (field === 'startTime' || field === 'endTime') {
      newValue = (target as HTMLInputElement).value;
    } else {
      newValue = (target as HTMLInputElement).value;
    }

    const contentItem = this.transcription.content[contentIndex];
    if (!contentItem) {
      this.editingField = null;
      this.originalValue = null;
      return;
    }

    const originalValue = this.originalValue !== null ? this.originalValue : (contentItem as any)[field];

    if (field === 'startTime' || field === 'endTime') {
      const newTimeMs = this.parseTimeInput(newValue);

      if (newTimeMs === null) {
        (contentItem as any)[field] = originalValue;
        this.sharedService.showNotification.next({
          message: 'Invalid format - MM:SS:MMM',
          type: 'ERROR',
        });
      } else if (originalValue !== newTimeMs) {
        (contentItem as any)[field] = newTimeMs;
        this.autoSaveContentItem(contentItem);

        if (field === 'startTime') {
          this.sortContentByStartTime();
        }
      }
    } else {
      (contentItem as any)[field] = newValue;

      if (originalValue !== newValue) {
        this.autoSaveContentItem(contentItem);
      }
    }

    this.editingField = null;
    this.originalValue = null;
  }

  onFileNameInput(event: Event) {
    if (!this.transcription) return;

    const target = event.target as HTMLInputElement;
    const newFileName = target.value;

    this.transcription.fileName = newFileName;
  }

  onFileNameBlur(event: Event) {
    if (!this.transcription) return;

    if (this.escapePressed) {
      this.escapePressed = false;
      this.editingFileName = false;
      return;
    }

    const target = event.target as HTMLInputElement;
    const newFileName = target.value;

    if (newFileName !== this.itemForEdit?.fileName) {
      this.transcription.fileName = newFileName;
      this.autoSaveFileName();
    }

    this.editingFileName = false;
  }

  addContentItem() {
    if (!this.transcription) return;

    const newContent: TranscriptionContentResponse = {
      id: '',
      text: 'New message...',
      startTime: 0,
      endTime: 1000,
      speaker: 'New Speaker',
      transcriptionId: this.transcription.id
    };

    if (!this.transcription.content) {
      this.transcription.content = [];
    }

    this.transcription.content.push(newContent);
    this.sortContentByStartTime();

    setTimeout(() => {
      const newIndex = this.transcription!.content!.findIndex(item => item === newContent);
      this.scrollToNewItem(newIndex);
      this.startEditing(newIndex, 'text');
    }, 100);
  }

  private scrollToNewItem(index: number) {
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      const chatMessages = chatContainer.querySelectorAll('.chat-message');
      if (chatMessages[index]) {
        const messageElement = chatMessages[index] as HTMLElement;
        const containerRect = chatContainer.getBoundingClientRect();
        const messageRect = messageElement.getBoundingClientRect();

        const scrollTop = chatContainer.scrollTop + messageRect.top - containerRect.top - (containerRect.height / 2) + (messageRect.height / 2);

        chatContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  }

  removeContentItem(index: number) {
    if (!this.transcription?.content) return;

    const contentItem = this.transcription.content[index];
    if (contentItem.id) {
      this.deleteContentItem(contentItem.id, index);
    } else {
      this.transcription.content.splice(index, 1);
    }
  }

  private createContentItem(contentItem: TranscriptionContentResponse) {
    if (!this.transcription?.id) {
      return;
    }

    const createRequest: CreateTranscriptionContentRequest = {
      text: contentItem.text,
      startTime: contentItem.startTime,
      endTime: contentItem.endTime,
      speaker: contentItem.speaker,
      transcriptionId: this.transcription.id
    };

    const apiCall = this.mode === 'admin'
      ? this.apiMgmtTranscriptions.createMgmtTranscriptionContent(createRequest)
      : this.apiClientTranscriptions.createClientTranscriptionContent(createRequest);

    apiCall
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response && response.length > 0) {
            if (this.transcription && this.transcription.content) {
              this.transcription.content = response;
              this.calculateTotalDuration();
            }
          }

          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
            type: 'SUCCESS',
          });
        },
        error: (error) => {
          this.sharedService.showNotification.next({
            message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
            type: 'ERROR',
          });
        }
      });
  }

  private deleteContentItem(contentId: string, index: number) {
    const apiCall = this.mode === 'admin'
      ? this.apiMgmtTranscriptions.deleteMgmtTranscriptionContent(contentId)
      : this.apiClientTranscriptions.deleteClientTranscriptionContent(contentId);

    apiCall
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (this.transcription?.content) {
            this.transcription.content.splice(index, 1);
          }
          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
            type: 'SUCCESS',
          });
        },
        error: (error) => {
          this.sharedService.showNotification.next({
            message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
            type: 'ERROR',
          });
        }
      });
  }

  private autoSaveContentItem(contentItem: TranscriptionContentResponse) {
    if (!contentItem.id) {
      this.createContentItem(contentItem);
      return;
    }

    const updateRequest: UpdateTranscriptionContentRequest = {
      text: contentItem.text,
      startTime: contentItem.startTime,
      endTime: contentItem.endTime,
      speaker: contentItem.speaker
    };

    const apiCall = this.mode === 'admin'
      ? this.apiMgmtTranscriptions.updateMgmtTranscriptionContent(updateRequest, contentItem.id!)
      : this.apiClientTranscriptions.updateClientTranscriptionContent(updateRequest, contentItem.id!);

    apiCall
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response && response.length > 0) {
            if (this.transcription && this.transcription.content) {
              this.transcription.content = response;
              this.calculateTotalDuration();
            }
          }

          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
            type: 'SUCCESS',
          });
        },
        error: (error) => {
          this.sharedService.showNotification.next({
            message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
            type: 'ERROR',
          });
        }
      });
  }

  private autoSaveFileName() {
    if (!this.transcription?.id) return;

    const updateRequest: UpdateTranscriptionRequest = {
      fileName: this.transcription.fileName
    };

    let apiCall;
    if (this.mode === 'admin') {
      apiCall = this.apiMgmtTranscriptions.updateMgmtTranscription(updateRequest, this.transcription.id!);
    } else {
      apiCall = this.apiClientTranscriptions.updateClientTranscription(updateRequest, this.transcription.id!);
    }

    apiCall
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
            type: 'SUCCESS',
          });
        },
        error: (error) => {
          this.sharedService.showNotification.next({
            message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
            type: 'ERROR',
          });
        }
      });
  }

  jumpToSegment(startTime: number | undefined) {
    if (startTime !== undefined) {
      this.videoTimeline?.jumpToTime(startTime);
    }
  }

  onTimelineTimeChanged(time: number) {
    this.timelineCurrentTime = time;
  }

  onTimelineDurationChanged(duration: number) {
    this.totalDuration = duration;
  }

  onTranscriptionSegmentClicked(contentIndex: number) {
    if (this.transcription?.content && contentIndex >= 0 && contentIndex < this.transcription.content.length) {
      this.currentSegmentIndex = contentIndex;
      setTimeout(() => {
        this.scrollToMessage(contentIndex);
        this.highlightMessage(contentIndex);
      }, 100);
    }
  }

  private scrollToMessage(index: number) {
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      const chatMessages = chatContainer.querySelectorAll('.chat-message');
      if (chatMessages[index]) {
        const messageElement = chatMessages[index] as HTMLElement;
        const containerRect = chatContainer.getBoundingClientRect();
        const messageRect = messageElement.getBoundingClientRect();

        const scrollTop = chatContainer.scrollTop + messageRect.top - containerRect.top - (containerRect.height / 2) + (messageRect.height / 2);

        chatContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  }

  private highlightMessage(index: number) {
    const existingHighlights = document.querySelectorAll('.message-highlighted');
    existingHighlights.forEach(el => el.classList.remove('message-highlighted'));

    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      const chatMessages = chatContainer.querySelectorAll('.chat-message');
      if (chatMessages[index]) {
        chatMessages[index].classList.add('message-highlighted');

        setTimeout(() => {
          chatMessages[index].classList.remove('message-highlighted');
        }, 2000);
      }
    }
  }

  clearForm() {
    this.actionRequested.emit({ action: 'FORM_CLOSED' });

    this.transcription = null;
    this.editingField = null;
    this.editingFileName = false;
    this.isFormVisible = false;
    this.timelineCurrentTime = 0;
    this.currentSegmentIndex = -1;
    this.totalDuration = 0;
  }


  onEnterKey(event: Event | KeyboardEvent) {
    const keyEvent = event as KeyboardEvent;
    if (!keyEvent.shiftKey) {
      event.preventDefault();
      this.escapePressed = false;
      (event.target as HTMLInputElement).blur();
    }
  }

  onEscapeKey(event: Event | KeyboardEvent) {
    event.preventDefault();

    this.escapePressed = true;

    if (this.editingField && this.transcription?.content) {
      const contentItem = this.transcription.content[this.editingField.contentIndex];

      const originalValue = this.originalValue !== null ? this.originalValue :
        this.itemForEdit?.content?.[this.editingField.contentIndex]?.[this.editingField.field as keyof TranscriptionContentResponse];

      if (contentItem && originalValue !== undefined) {
        (contentItem as any)[this.editingField.field] = originalValue;
      }
    } else if (this.editingFileName && this.transcription) {
      this.transcription.fileName = this.itemForEdit?.fileName || '';
    }

    (event.target as HTMLInputElement | HTMLTextAreaElement).blur();
  }

  openFindReplaceDialog() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      data: {
        type: 'custom-fields',
        fields: [
          {
            type: 'textfield',
            key: 'findText',
            label: this.translate.instant('TRANSCRIPTIONS.FIND_TEXT'),
            required: true
          },
          {
            type: 'textfield',
            key: 'replaceText',
            label: this.translate.instant('TRANSCRIPTIONS.REPLACE_TEXT'),
            required: true
          }
        ],
        title: this.translate.instant('TRANSCRIPTIONS.FIND_AND_REPLACE'),
        mode: 'EDIT',
        skipTranscriptionValidation: true
      }
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result && result.data) {
          this.performFindAndReplace(result.data.findText, result.data.replaceText);
        }
      });
  }

  private performFindAndReplace(findText: string, replaceText: string) {
    if (!this.transcription?.id || !findText) {
      return;
    }

    const request: FindAndReplaceRequest = {
      findText: findText.trim(),
      replaceText: replaceText || ''
    };

    const apiCall = this.mode === 'admin'
      ? this.apiMgmtTranscriptions.findAndReplaceMgmtTranscriptionContent(request, this.transcription.id)
      : this.apiClientTranscriptions.findAndReplaceClientTranscriptionContent(request, this.transcription.id);

    apiCall
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.transcription = response;
          this.calculateTotalDuration();

          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
            type: 'SUCCESS',
          });

          this.actionRequested.emit({
            action: 'TRANSCRIPTION_UPDATED',
            transcription: response
          });
        },
        error: (error) => {
          this.sharedService.showNotification.next({
            message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
            type: 'ERROR',
          });
        }
      });
  }

  isVideoFile(fileUrl?: string): boolean {
    if (this.transcription?.type === 'RECORDING') return false;
    if (!fileUrl) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
    const lowerUrl = fileUrl.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext));
  }

  onVideoVisibilityToggled(isVisible: boolean) {
    this.isVideoVisible = isVisible;
  }

  onCaptionsToggled(isEnabled: boolean) {
    this.captionsEnabled = isEnabled;
  }

  onMergedTextToggled(isMerged: boolean) {
    this.isTextMerged = isMerged;
  }

  get hasTranscriptionContent(): boolean {
    return !!(this.transcription?.content && this.transcription.content.length > 0);
  }

  get transcriptionSourceUrl(): string | null {
    // TEMP: sourceUrl isn't in the generated api-transcription SDK yet, even though the backend
    // response already carries it for YouTube/URL-sourced transcriptions.
    return (this.transcription as unknown as { sourceUrl?: string })?.sourceUrl || null;
  }

  get mergedText(): string {
    return (this.transcription?.content ?? [])
      .map(segment => segment.text?.trim())
      .filter((text): text is string => !!text)
      .join(' ');
  }

  ngOnDestroy() {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  // Recording methods (for admin mode)
  openCreateTranscriptionDialog() {
    this.actionRequested.emit({ action: 'CREATE_TRANSCRIPTION' });
  }

  /** Tools (auto-tag) — admin mode only */
  openTools() {
    const dialogRef = this.dialog.open(TranscriptionsToolsComponent, {
      width: '600px',
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result && result.data) {
          switch (result.data.tool) {
            case 'AUTO_TAG': {
              this.autoTagTranscriptions(result.data.dateFrom, result.data.dateTo);
              break;
            }
          }
        }
      });
  }

  private autoTagTranscriptions(dateFrom?: Date, dateTo?: Date) {
    this.apiLoadingTools = true;

    // The auto-tag endpoint expects epoch millis; default to yesterday -> today when omitted.
    const from = dateFrom
      ? new Date(new Date(dateFrom).setHours(0, 0, 0, 0)).getTime()
      : new Date(new Date(new Date().setDate(new Date().getDate() - 1)).setHours(0, 0, 0, 0)).getTime();
    const to = dateTo
      ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).getTime()
      : new Date(new Date().setHours(23, 59, 59, 999)).getTime();

    const body: ManagementAutoTagTranscriptionsRequest = { dateFrom: from, dateTo: to };

    this.apiMgmtTranscriptions
      .autoTagTranscriptions(body)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.apiLoadingTools = false;
          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.ACTION_COMPLETE'),
            type: 'SUCCESS',
          });
          // Reload the transcriptions list so the freshly tagged items refresh
          this.sharedService.requestActionInComponent.next({
            componentName: 'TranscriptionsFiltersComponent',
            action: 'FILTER_TRIGGER',
          });
        },
        error: () => {
          this.apiLoadingTools = false;
          this.sharedService.showNotification.next({
            message: this.translate.instant('NOTIFICATIONS.ACTION_ERROR'),
            type: 'ERROR',
          });
        },
      });
  }

  async toggleRecording() {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }

  private formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  private updateRecordingDuration() {
    if (this.isRecording && this.recordingStartTime) {
      const elapsedSeconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
      this.recordingDuration = this.formatDuration(elapsedSeconds);
    }
  }

  private async startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 44100,
          channelCount: 2,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      this.audioChunks = [];
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.processRecording();
        stream.getTracks().forEach(track => track.stop());
      };

      this.mediaRecorder.start(1000);
      this.isRecording = true;
      this.recordingStartTime = Date.now();
      this.recordingDuration = '00:00';

      this.recordingTimer = setInterval(() => {
        this.updateRecordingDuration();
      }, 1000);
    } catch (error: any) {
      this.sharedService.showNotification.next({
        message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
        type: 'ERROR',
      });
    }
  }

  private stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  private async processRecording() {
    if (this.audioChunks.length === 0) {
      return;
    }

    this.isProcessingRecording = true;

    try {
      const audioBlob = new Blob(this.audioChunks, {
        type: this.mediaRecorder?.mimeType || 'audio/webm'
      });

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `recording-${timestamp}.webm`;
      const audioFile = new File([audioBlob], fileName, {
        type: audioBlob.type
      });

      this.actionRequested.emit({
        action: 'CREATE_TRANSCRIPTION_FROM_RECORDING',
        audioFile: audioFile
      });

    } catch (error: any) {
      this.sharedService.showNotification.next({
        message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
        type: 'ERROR',
      });
    } finally {
      this.isProcessingRecording = false;
      this.audioChunks = [];
    }
  }
}
